import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { cn, formatCurrency, formatPercent, formatNumber, getStatusColor } from '../../lib/utils';
import { DollarSign, Percent, Clock, CheckCircle2, AlertCircle, TrendingUp, FileText, Users, Activity, Calendar as CalendarIcon, RefreshCw, ArrowRight, Sparkles, Shield, Send, Search, XCircle, CreditCard, Zap, GripVertical, X } from 'lucide-react';
import { format } from 'date-fns';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Area, AreaChart } from 'recharts';
import { useNavigate } from 'react-router-dom';
import ActivityFeed from '../../components/shared/ActivityFeed';
import WorkflowTracker from '../../components/shared/WorkflowTracker';
import AIMorningSummary from '../../components/shared/AIMorningSummary';
import WeeklySummaryCard from '../../components/shared/WeeklySummaryCard';
import FirstRunBanner from '../../components/shared/FirstRunBanner';
import EmptyStateGuide from '../../components/shared/EmptyStateGuide';
import RevQOnboardingLightbox, { LIGHTBOX_DISMISSED_KEY as WORKFLOW_LIGHTBOX_KEY } from '../../components/revq/RevQOnboardingLightbox';

import { useIntegrationMode } from '../../contexts/IntegrationModeContext';
import { useEnterprise } from '../../contexts/EnterpriseContext';
import { useAppMode } from '../../contexts/AppModeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CHART_COLORS = { primary: '#000000', secondary: '#10b981', success: '#10b981', warning: '#f59e0b', error: '#ef4444', muted: '#6b7280' };
/* ── Draggable container wrapper ── */
const DraggableCard = ({ id, children, onDragStart, onDragOver, onDrop, isDragging }) => (
  <div
    draggable
    onDragStart={(e) => onDragStart(e, id)}
    onDragOver={onDragOver}
    onDrop={(e) => onDrop(e, id)}
    className={cn("transition-all duration-200 group relative", isDragging ? "opacity-40 scale-[0.98]" : "opacity-100")}
    data-testid={`draggable-${id}`}
  >
    <div className="absolute top-3 right-3 z-10 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing" data-testid={`drag-handle-${id}`}>
      <GripVertical style={{ width: 14, height: 14, color: '#ccc' }} />
    </div>
    {children}
  </div>
);

const RevQDashboard = () => {
  const { isDemo } = useAppMode();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [role, setRole] = useState('manager');
  const integration = useIntegrationMode();
  const enterprise = useEnterprise();
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/revq/dashboard/all`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-32 bg-slate-200 rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="revq-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] tracking-tight text-slate-900">
            {role === 'physician' ? 'Provider Dashboard' : role === 'biller' ? 'Billing Dashboard' : 'Executive Dashboard'}
          </h1>
          <p className="text-slate-500 mt-1">{format(new Date(), 'EEEE, MMMM d, yyyy')}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              if (enterprise?.isEnterprise) {
                const eRole = enterprise.currentRole;
                if (eRole === 'supervisor') navigate('/revq/supervisor');
                else if (eRole === 'coder') navigate('/revq/coder');
                else if (eRole === 'denials') navigate('/revq/denials-focus');
                else navigate('/revq/billing-focus');
              } else {
                navigate('/revq/billing-focus');
              }
            }}
            className="flex items-center gap-2 transition-colors hover:opacity-90"
            style={{ height: 40, padding: '0 20px', borderRadius: 8, background: '#111', color: '#fff', fontSize: 13, fontWeight: 500, border: 'none', cursor: 'pointer' }}
            data-testid="dashboard-enter-billing-focus"
          >
            <Zap style={{ width: 14, height: 14 }} />
            Enter Billing Focus
            <ArrowRight style={{ width: 14, height: 14, opacity: 0.6 }} />
          </button>
          <Select value={role} onValueChange={(v) => {
            setRole(v);
            if (enterprise?.isEnterprise && enterprise?.setRole) {
              const roleMap = { coder_view: 'coder', biller_view: 'biller', denials_view: 'denials', supervisor_view: 'supervisor', admin_view: 'admin' };
              if (roleMap[v]) enterprise.setRole(roleMap[v]);
            }
          }}>
            <SelectTrigger className="w-44" data-testid="dashboard-role-filter">
              <SelectValue placeholder="View as" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="manager">Executive View</SelectItem>
              <SelectItem value="biller">Biller View</SelectItem>
              <SelectItem value="physician">Provider View</SelectItem>
              {enterprise?.isEnterprise && (
                <>
                  <div style={{ height: 1, background: '#f0f0f0', margin: '4px 0' }} />
                  <div style={{ padding: '4px 8px', fontSize: 9, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Enterprise Roles</div>
                  <SelectItem value="coder_view">
                    <span className="flex items-center gap-2"><span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3b82f6', display: 'inline-block' }} />Coder View</span>
                  </SelectItem>
                  <SelectItem value="biller_view">
                    <span className="flex items-center gap-2"><span style={{ width: 6, height: 6, borderRadius: '50%', background: '#6b5ce7', display: 'inline-block' }} />Biller View</span>
                  </SelectItem>
                  <SelectItem value="denials_view">
                    <span className="flex items-center gap-2"><span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />Denials View</span>
                  </SelectItem>
                  <SelectItem value="supervisor_view">
                    <span className="flex items-center gap-2"><span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />Supervisor View</span>
                  </SelectItem>
                  <SelectItem value="admin_view">
                    <span className="flex items-center gap-2"><span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f59e0b', display: 'inline-block' }} />Admin View</span>
                  </SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <CalendarIcon className="h-4 w-4" />{dateRange.from ? format(dateRange.from, 'MMM d') : 'Date Range'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar mode="range" selected={dateRange} onSelect={setDateRange} numberOfMonths={2} />
            </PopoverContent>
          </Popover>
          <Button variant="ghost" size="icon" onClick={fetchData}><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Enterprise Team Summary */}
      {enterprise?.isEnterprise && (
        <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: 12, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 16 }}
          data-testid="enterprise-team-summary">
          <div style={{ width: 36, height: 36, borderRadius: 8, background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Users style={{ width: 16, height: 16, color: '#6b5ce7' }} />
          </div>
          <div style={{ flex: 1, display: 'flex', gap: 24, alignItems: 'center' }}>
            <div>
              <p style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Team summary</p>
              <p style={{ fontSize: 13, fontWeight: 500, color: '#111', marginTop: 2 }}>
                {enterprise.kpi?.openClaims || 34} claims in queue &middot; 12 in review &middot; {enterprise.kpi?.pendingOver30 || 7} needs attention
              </p>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 11, color: '#888' }}>First-pass rate this week:</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#16a34a' }}>{enterprise.kpi?.firstPassRate || 96.2}%</span>
            </div>
          </div>
        </div>
      )}

      {role === 'manager' && <ManagerDashboard data={data} />}
      {role === 'biller' && <BillerDashboard data={data} integration={integration} />}
      {role === 'physician' && <PhysicianDashboard data={data} />}

      {/* Enterprise role-specific summaries */}
      {role === 'coder_view' && enterprise?.isEnterprise && (
        <div className="space-y-4" data-testid="coder-dashboard-view">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card><CardContent className="p-5">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Charts to code</p>
              <p className="text-3xl font-bold mt-2">14</p>
              <p className="text-xs text-muted-foreground mt-1">2 high priority</p>
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Coded today</p>
              <p className="text-3xl font-bold mt-2">8</p>
              <p className="text-xs text-emerald-600 mt-1">Avg. 3.8 min per chart</p>
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Coding accuracy</p>
              <p className="text-3xl font-bold mt-2 text-emerald-600">97.2%</p>
              <p className="text-xs text-muted-foreground mt-1">0 coder-caused denials this week</p>
            </CardContent></Card>
          </div>
          <Card><CardContent className="p-5">
            <h3 className="font-semibold text-sm mb-3">Pending charts by provider</h3>
            <div className="space-y-2">
              {(enterprise.providers || []).map((p, i) => (
                <div key={p} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                  <span className="text-sm">{p}</span>
                  <span className="text-sm font-medium">{[5, 6, 3][i] || 2} charts</span>
                </div>
              ))}
            </div>
          </CardContent></Card>
        </div>
      )}

      {role === 'denials_view' && enterprise?.isEnterprise && (
        <div className="space-y-4" data-testid="denials-dashboard-view">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card><CardContent className="p-5">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Open denials</p>
              <p className="text-3xl font-bold mt-2 text-red-600">{enterprise.deniedClaims?.length || 3}</p>
              <p className="text-xs text-muted-foreground mt-1">${(enterprise.deniedClaims || []).reduce((s, c) => s + c.amount, 0).toLocaleString()} at risk</p>
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Resolved this month</p>
              <p className="text-3xl font-bold mt-2 text-emerald-600">9</p>
              <p className="text-xs text-muted-foreground mt-1">$4,120 recovered</p>
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Avg. days to resolve</p>
              <p className="text-3xl font-bold mt-2">12</p>
              <p className="text-xs text-amber-600 mt-1">Target: &lt;10 days</p>
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Appeal success rate</p>
              <p className="text-3xl font-bold mt-2 text-emerald-600">73%</p>
              <p className="text-xs text-muted-foreground mt-1">Industry avg: 60%</p>
            </CardContent></Card>
          </div>
          <Card><CardContent className="p-5">
            <h3 className="font-semibold text-sm mb-3">Top denial reasons this month</h3>
            <div className="space-y-2">
              {(enterprise.denialTrends || []).slice(0, 4).map(d => (
                <div key={d.category} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                  <span className="text-sm">{d.category}</span>
                  <div className="flex items-center gap-2">
                    <div style={{ width: Math.min(120, d.count * 18), height: 6, borderRadius: 3, background: d.category === 'Medical necessity' ? '#fecaca' : d.category === 'Coding error' ? '#fde68a' : '#fed7aa' }} />
                    <span className="text-sm font-medium w-6 text-right">{d.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent></Card>
        </div>
      )}

      {role === 'supervisor_view' && enterprise?.isEnterprise && (
        <div className="space-y-4" data-testid="supervisor-dashboard-view">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card><CardContent className="p-5">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">First-pass rate</p>
              <p className="text-3xl font-bold mt-2 text-emerald-600">{enterprise.kpi?.firstPassRate}%</p>
              <p className="text-xs text-emerald-600 mt-1">+{enterprise.kpi?.firstPassTrend}% vs last month</p>
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Days in A/R</p>
              <p className="text-3xl font-bold mt-2">{enterprise.kpi?.daysInAR}</p>
              <p className="text-xs text-muted-foreground mt-1">Target: &lt;{enterprise.kpi?.arTarget} days</p>
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Denial rate</p>
              <p className="text-3xl font-bold mt-2">{enterprise.kpi?.denialRate}%</p>
              <p className="text-xs text-muted-foreground mt-1">Industry avg: {enterprise.kpi?.industryAvg}%</p>
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Team size</p>
              <p className="text-3xl font-bold mt-2">{enterprise.teamMembers?.filter(m => m.status === 'active').length || 0}</p>
              <p className="text-xs text-muted-foreground mt-1">active members</p>
            </CardContent></Card>
          </div>
          <Card><CardContent className="p-5">
            <h3 className="font-semibold text-sm mb-3">Team at a glance</h3>
            <div className="space-y-2">
              {(enterprise.teamPerf || []).map(m => (
                <div key={m.name} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                  <div>
                    <span className="text-sm font-medium">{m.name}</span>
                    <span className="text-xs text-muted-foreground ml-2">{m.role}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs">
                    <span>{m.claimsProcessed} claims</span>
                    {m.firstPass !== null && <span style={{ color: m.firstPass >= 95 ? '#16a34a' : m.firstPass >= 85 ? '#d97706' : '#ef4444' }}>{m.firstPass}%</span>}
                    <span className="text-muted-foreground">{m.openItems} open</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent></Card>
        </div>
      )}

      {(role === 'biller_view' || role === 'admin_view') && enterprise?.isEnterprise && (
        <BillerDashboard data={data} integration={integration} />
      )}
    </div>
  );
};

const KPICard = ({ title, value, unit = '', target, status, icon: Icon, trend, trendValue }) => {
  const statusColors = getStatusColor(status);
  const formatValue = (val, u) => {
    if (u === '$') return formatCurrency(val);
    if (u === '%') return formatPercent(val);
    if (u === 'days') return `${val} days`;
    return val;
  };

  return (
    <Card className="dashboard-card">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground font-medium">{title}</p>
            <span className="text-2xl font-bold font-['Plus_Jakarta_Sans']">{formatValue(value, unit)}</span>
            {target && <p className="text-xs text-muted-foreground">Target: {formatValue(target, unit)}</p>}
          </div>
          <div className="flex flex-col items-end gap-2">
            {Icon && <div className={cn("h-10 w-10 rounded-lg flex items-center justify-center", statusColors.bg)}><Icon className={cn("h-5 w-5", statusColors.text)} /></div>}
            {trendValue && <div className={cn("flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full", trend === 'up' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700')}>{trendValue}</div>}
          </div>
        </div>
        {status && (
          <div className="mt-4 w-full bg-slate-100 rounded-full h-1.5">
            <div className={cn("h-1.5 rounded-full", status === 'success' ? 'bg-emerald-500' : status === 'warning' ? 'bg-amber-500' : status === 'error' ? 'bg-red-500' : 'bg-gray-500')}
              style={{ width: `${Math.min((value / (target || value)) * 100, 100)}%` }} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const DASHBOARD_STORAGE_KEY = 'revq_dashboard_order';
const DEFAULT_ORDER = ['ai_summary', 'handled_review', 'kpis', 'forecast_activity', 'pipeline', 'revenue_payer', 'recent_claims'];

const ManagerDashboard = ({ data }) => {
  const navigate = useNavigate();
  const integration = useIntegrationMode();

  /* ── Workflow lightbox state ── */
  const [showWorkflow, setShowWorkflow] = useState(() => {
    try { return localStorage.getItem(WORKFLOW_LIGHTBOX_KEY) !== 'true'; } catch { return true; }
  });

  /* ── Drag-and-drop for movable containers ── */
  const [cardOrder, setCardOrder] = useState(() => {
    try {
      const saved = localStorage.getItem(DASHBOARD_STORAGE_KEY);
      if (saved) { const parsed = JSON.parse(saved); if (Array.isArray(parsed) && parsed.length === DEFAULT_ORDER.length) return parsed; }
    } catch {}
    return DEFAULT_ORDER;
  });
  const [dragId, setDragId] = useState(null);

  useEffect(() => {
    try { localStorage.setItem(DASHBOARD_STORAGE_KEY, JSON.stringify(cardOrder)); } catch {}
  }, [cardOrder]);

  if (!data) return null;
  const { executive, claims, payers, compliance } = data;

  const onDragStart = (e, id) => { setDragId(id); e.dataTransfer.effectAllowed = 'move'; };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onDrop = (e, targetId) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    setCardOrder(prev => {
      const fromIdx = prev.indexOf(dragId);
      const toIdx = prev.indexOf(targetId);
      const next = [...prev];
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, dragId);
      return next;
    });
    setDragId(null);
  };

  const REVQ_AI_ACTIVITY = [
    { patient: 'John Smith', codes: 'E11.9, I10 \u2014 99214', status: 'Clean \u2014 submitted', auto: true },
    { patient: 'Maria Garcia', codes: 'J45.20, J06.9 \u2014 99213', status: 'Clean \u2014 submitted', auto: true },
    { patient: 'Emily Davis', codes: 'I10, E78.5 \u2014 99215', status: 'Clean \u2014 submitted', auto: true },
    { patient: 'James Taylor', codes: '99214 \u2014 CPT flagged', status: 'Needs review', auto: false, flag: 'CPT 99214 flagged \u2014 Blue Cross historically denies this code without a supporting diagnosis code for hypertension (I10). Add I10 to resolve.' },
  ];

  const handledCount = REVQ_AI_ACTIVITY.filter(c => c.auto).length;
  const flaggedCount = REVQ_AI_ACTIVITY.filter(c => !c.auto).length;

  const PIPELINE_STAGES = [
    { id: 'draft', label: 'Draft', count: 2, amount: 440, color: 'bg-slate-500', bgLight: 'bg-slate-50 border-slate-200', text: 'text-slate-700' },
    { id: 'submitted', label: 'Submitted', count: 2, amount: 1600, color: 'bg-blue-500', bgLight: 'bg-blue-50 border-blue-200', text: 'text-blue-700' },
    { id: 'accepted', label: 'Accepted', count: 2, amount: 480, color: 'bg-emerald-500', bgLight: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700' },
    { id: 'denied', label: 'Denied', count: 1, amount: 450, color: 'bg-red-500', bgLight: 'bg-red-50 border-red-200', text: 'text-red-700' },
    { id: 'paid', label: 'Paid', count: 3, amount: 888, color: 'bg-emerald-600', bgLight: 'bg-emerald-50 border-emerald-300', text: 'text-emerald-800' },
  ];
  const totalPipeline = PIPELINE_STAGES.reduce((s, st) => s + st.count, 0);

  const FORECAST_DATA = [
    { period: '30 Days', projected: 48500, confidence: 92 },
    { period: '60 Days', projected: 91200, confidence: 78 },
    { period: '90 Days', projected: 128400, confidence: 65 },
  ];

  /* ── Renderable sections ── */
  const sections = {
    ai_summary: (
      <>
        <AIMorningSummary
          app="revq"
          text={`RevQ reviewed 4 claims from yesterday's encounters. ${handledCount} passed validation. ${flaggedCount} flagged: missing modifier on CPT 99214 for James Taylor \u2014 likely denial reason: incomplete documentation. Suggested fix attached.`}
        />
        {integration.shouldShowWeeklySummary() && (
          <WeeklySummaryCard app="revq" onDismiss={integration.dismissWeeklySummary} />
        )}
        {integration.isFirstRun('revq') && (
          <FirstRunBanner
            app="revq"
            onLoadSchedule={() => { navigate('/revq/encounters'); integration.dismissFirstRun('revq'); }}
            onExploreDemo={() => integration.dismissFirstRun('revq')}
          />
        )}
      </>
    ),
    handled_review: (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="handled-review-row">
        {/* Handled by RevQ */}
        <Card data-testid="revq-handled-section">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              <CardTitle className="text-base font-semibold">Handled by RevQ</CardTitle>
              <Badge className="bg-emerald-50 text-emerald-700 text-[10px] border-0">{handledCount} auto-processed</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {REVQ_AI_ACTIVITY.filter(c => c.auto).map((item, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-2.5 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`handled-claim-${i}`}>
                <div className="flex items-center gap-3">
                  <div className="h-7 w-7 rounded-full bg-emerald-50 flex items-center justify-center">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-800">{item.patient}</p>
                    <p className="text-[10px] text-muted-foreground">{item.codes}</p>
                  </div>
                </div>
                <Badge className="bg-emerald-50 text-emerald-700 text-[10px] border-0">{item.status}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Needs Your Review */}
        <Card data-testid="revq-review-section">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              <CardTitle className="text-base font-semibold">Needs your review</CardTitle>
              <Badge className="bg-amber-50 text-amber-700 text-[10px] border-0">{flaggedCount} flagged</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {REVQ_AI_ACTIVITY.filter(c => !c.auto).map((item, i) => (
              <div key={i} className="p-3 rounded-lg border border-amber-200 bg-amber-50/30" data-testid={`flagged-claim-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-slate-800">{item.patient}</p>
                  <Badge className="bg-amber-100 text-amber-700 text-[10px] border-0">Review needed</Badge>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">{item.flag}</p>
                <div className="flex items-center gap-2 mt-2.5">
                  <button
                    className="flex items-center gap-1"
                    style={{ height: 26, padding: '0 10px', borderRadius: 6, background: '#111', color: '#fff', fontSize: 11, fontWeight: 500, border: 'none', cursor: 'pointer' }}
                    data-testid={`fix-claim-${i}`}
                  >
                    Add code
                  </button>
                  <button
                    className="flex items-center gap-1"
                    style={{ height: 26, padding: '0 10px', borderRadius: 6, background: 'transparent', color: '#666', fontSize: 11, fontWeight: 500, border: '1px solid #e0e0e0', cursor: 'pointer' }}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            ))}
            {flaggedCount === 0 && (
              <div className="flex items-center justify-center py-6 text-center">
                <div>
                  <CheckCircle2 className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No items need review</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    ),
    kpis: (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard title="Net Collections" value={executive.net_collections} unit="$" icon={DollarSign} status="success" trend="up" trendValue="+5.2%" />
        <KPICard title="Collection Rate" value={executive.collection_rate} unit="%" target={75} icon={Percent} status={executive.collection_rate >= 75 ? 'success' : 'warning'} />
        <KPICard title="Days in A/R" value={executive.avg_days_in_ar} unit="days" target={35} icon={Clock} status={executive.avg_days_in_ar <= 35 ? 'success' : 'warning'} />
        <KPICard title="Denial Rate" value={claims.denial_rate} unit="%" target={5} icon={AlertCircle} status={claims.denial_rate <= 5 ? 'success' : 'error'} />
      </div>
    ),
    forecast_activity: (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2" data-testid="revenue-forecast">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-violet-500" />
              <CardTitle className="text-lg font-semibold">AI Revenue Forecast</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              {FORECAST_DATA.map((f) => (
                <div key={f.period} className="p-4 rounded-xl border border-slate-200 hover:border-slate-300 transition-all" data-testid={`forecast-${f.period.replace(' ', '-').toLowerCase()}`}>
                  <p className="text-xs text-muted-foreground font-medium">{f.period}</p>
                  <p className="text-2xl font-bold mt-1">${(f.projected / 1000).toFixed(1)}k</p>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className={cn("h-full rounded-full", f.confidence >= 80 ? "bg-emerald-500" : f.confidence >= 60 ? "bg-amber-500" : "bg-red-400")} style={{ width: `${f.confidence}%` }} />
                    </div>
                    <span className={cn("text-[10px] font-medium", f.confidence >= 80 ? "text-emerald-600" : f.confidence >= 60 ? "text-amber-600" : "text-red-500")}>{f.confidence}%</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">Confidence</p>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-3 flex items-center gap-1"><Sparkles className="h-3 w-3 text-violet-400" /> Projected based on claim aging, payer patterns & seasonal trends</p>
          </CardContent>
        </Card>

        <ActivityFeed app="revq" maxItems={5} />
      </div>
    ),
    pipeline: (
      <Card data-testid="claim-status-tracker">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold">Claim Pipeline</CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">{totalPipeline} total claims</Badge>
              <button onClick={() => setShowWorkflow(true)} className="text-xs text-violet-600 hover:underline" style={{ background: 'none', border: 'none', cursor: 'pointer' }} data-testid="open-workflow-lightbox-btn">
                View workflow guide
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex h-3 rounded-full overflow-hidden mb-4">
            {PIPELINE_STAGES.map(stage => (
              <div key={stage.id} className={cn("transition-all", stage.color)} style={{ width: `${(stage.count / totalPipeline) * 100}%` }} title={`${stage.label}: ${stage.count}`} />
            ))}
          </div>
          <div className="grid grid-cols-5 gap-3">
            {PIPELINE_STAGES.map(stage => (
              <button key={stage.id} onClick={() => navigate('/revq/claims')} className={cn("p-3 rounded-xl border text-center hover:shadow-md transition-all cursor-pointer", stage.bgLight)} data-testid={`pipeline-${stage.id}`}>
                <p className="text-2xl font-bold">{stage.count}</p>
                <p className="text-xs font-medium mt-0.5">{stage.label}</p>
                <p className={cn("text-[10px] mt-1 font-medium", stage.text)}>${stage.amount.toLocaleString()}</p>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-3">
            {PIPELINE_STAGES.map((stage, i) => (
              <div key={stage.id} className="flex items-center gap-1">
                {i > 0 && <ArrowRight className="h-3 w-3 text-slate-300" />}
                <div className={cn("h-2 w-2 rounded-full", stage.color)} />
                <span className="text-[10px] text-muted-foreground">{stage.label}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    ),
    revenue_payer: (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-lg font-semibold">Revenue Trend</CardTitle></CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={executive.revenue_trend}>
                  <defs>
                    <linearGradient id="colorGross" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#000" stopOpacity={0.1}/><stop offset="95%" stopColor="#000" stopOpacity={0}/></linearGradient>
                    <linearGradient id="colorNet" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.1}/><stop offset="95%" stopColor="#10b981" stopOpacity={0}/></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" tickFormatter={(v) => `$${v/1000}k`} />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="gross" name="Gross Revenue" stroke="#000" strokeWidth={2} fillOpacity={1} fill="url(#colorGross)" />
                  <Area type="monotone" dataKey="net" name="Net Collections" stroke="#10b981" strokeWidth={2} fillOpacity={1} fill="url(#colorNet)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg font-semibold">Payer Mix</CardTitle></CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={payers.payer_mix} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={2} dataKey="value" nameKey="name">
                    {payers.payer_mix.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={[CHART_COLORS.primary, CHART_COLORS.secondary, CHART_COLORS.success, CHART_COLORS.warning, CHART_COLORS.error, CHART_COLORS.muted][index % 6]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => `${v.toFixed(1)}%`} />
                  <Legend layout="vertical" align="right" verticalAlign="middle" />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    ),
    recent_claims: (
      <Card>
        <CardHeader><CardTitle className="text-lg font-semibold">Recent Claims</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Claim ID</TableHead><TableHead>Patient</TableHead><TableHead>Payer</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {claims.recent_claims.slice(0, 5).map((claim) => {
                const colors = getStatusColor(claim.status);
                return (
                  <TableRow key={claim.id}>
                    <TableCell className="font-mono text-sm">{claim.id}</TableCell>
                    <TableCell className="font-medium">{claim.patient}</TableCell>
                    <TableCell className="text-muted-foreground">{claim.payer}</TableCell>
                    <TableCell className="text-right font-medium">${claim.amount.toFixed(2)}</TableCell>
                    <TableCell><Badge className={cn("capitalize", colors.bg, colors.text)}>{claim.status}</Badge></TableCell>
                    <TableCell className="text-muted-foreground">{claim.date}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    ),
  };

  return (
    <div className="space-y-6">
      {/* Onboarding Lightbox */}
      <RevQOnboardingLightbox open={showWorkflow} onClose={() => setShowWorkflow(false)} />

      {cardOrder.map(id => (
        <DraggableCard key={id} id={id} onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop} isDragging={dragId === id}>
          {sections[id]}
        </DraggableCard>
      ))}
    </div>
  );
};

const BillerDashboard = ({ data, integration }) => {
  const navigate = useNavigate();
  if (!data) return null;
  const { claims, executive } = data;

  return (
    <div className="space-y-6">
      {/* Enter Billing Focus */}
      <div className="flex items-center justify-between p-4 rounded-xl" style={{ background: '#f5f3ff', border: '0.5px solid #e0dff5' }}>
        <div>
          <p style={{ fontSize: 14, fontWeight: 500, color: '#111' }}>Ready to process claims?</p>
          <p style={{ fontSize: 12, color: '#888' }}>Enter Billing Focus for distraction-free claim review and submission</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/revq/batch-scrub')}
            className="flex items-center gap-2 transition-colors hover:opacity-90"
            style={{ height: 40, padding: '0 16px', borderRadius: 8, background: '#fff', color: '#111', fontSize: 13, fontWeight: 500, border: '1px solid #e0dff5', cursor: 'pointer' }}
            data-testid="dashboard-batch-scrub"
          >
            <Sparkles style={{ width: 14, height: 14 }} />
            Batch Scrub
          </button>
          <button
            onClick={() => navigate('/revq/billing-focus')}
            className="flex items-center gap-2 transition-colors hover:opacity-90"
            style={{ height: 40, padding: '0 20px', borderRadius: 8, background: '#111', color: '#fff', fontSize: 13, fontWeight: 500, border: 'none', cursor: 'pointer' }}
            data-testid="biller-enter-billing-focus"
          >
            <Zap style={{ width: 14, height: 14 }} />
            Enter Billing Focus
            <ArrowRight style={{ width: 14, height: 14, opacity: 0.6 }} />
          </button>
        </div>
      </div>
      {/* AI Summary for Biller */}
      <AIMorningSummary
        app="revq"
        text={`Today's priority: ${claims.claims_rejected || 3} rejected claims need resubmission. Clean claim rate is ${claims.clean_claim_rate}% \u2014 ${claims.clean_claim_rate >= 95 ? 'on target' : 'below target, review coding patterns'}. Avg turnaround: ${claims.avg_turnaround_days} days.`}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard title="Claims Submitted" value={claims.total_submitted} icon={FileText} status="info" trend="up" trendValue="+12" />
        <KPICard title="Clean Claim Rate" value={claims.clean_claim_rate} unit="%" target={95} icon={CheckCircle2} status={claims.clean_claim_rate >= 95 ? 'success' : 'warning'} />
        <KPICard title="Denial Rate" value={claims.denial_rate} unit="%" target={5} icon={AlertCircle} status={claims.denial_rate <= 5 ? 'success' : 'error'} />
        <KPICard title="Avg. Turnaround" value={claims.avg_turnaround_days} unit="days" target={14} icon={Clock} status={claims.avg_turnaround_days <= 14 ? 'success' : 'warning'} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-emerald-50 border-emerald-200"><CardContent className="p-4"><div className="text-emerald-700"><p className="text-sm">Paid</p><p className="text-2xl font-bold">{formatNumber(claims.claims_paid)}</p></div></CardContent></Card>
        <Card className="bg-amber-50 border-amber-200"><CardContent className="p-4"><div className="text-amber-700"><p className="text-sm">Pending</p><p className="text-2xl font-bold">{formatNumber(claims.claims_pending)}</p></div></CardContent></Card>
        <Card className="bg-red-50 border-red-200"><CardContent className="p-4"><div className="text-red-700"><p className="text-sm">Rejected</p><p className="text-2xl font-bold">{formatNumber(claims.claims_rejected)}</p></div></CardContent></Card>
        <Card className="bg-blue-50 border-blue-200"><CardContent className="p-4"><div className="text-blue-700"><p className="text-sm">Appealed</p><p className="text-2xl font-bold">{formatNumber(claims.claims_appealed)}</p></div></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg font-semibold">Denial Reasons</CardTitle></CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={claims.denial_reasons}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="reason" tick={{ fontSize: 11 }} stroke="#94a3b8" angle={-15} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <Tooltip />
                <Bar dataKey="count" name="Count" fill={CHART_COLORS.error} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const PhysicianDashboard = ({ data }) => {
  if (!data) return null;
  const { providers } = data;
  const currentProvider = providers.providers[0];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard title="Total Charges" value={currentProvider.charges} unit="$" icon={DollarSign} status="info" trend="up" trendValue="+8.3%" />
        <KPICard title="Collections" value={currentProvider.collections} unit="$" icon={TrendingUp} status="success" />
        <KPICard title="Patient Visits" value={currentProvider.visits} icon={Users} status="info" trend="up" trendValue="+24" />
        <KPICard title="RVUs" value={currentProvider.rvu} icon={Activity} status="success" />
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg font-semibold">E/M Level Distribution</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-3">
            {providers.coding_distribution.map((level) => (
              <div key={level.level} className="flex items-center gap-3">
                <span className="text-sm font-mono w-16 text-muted-foreground">{level.level}</span>
                <div className="flex-1 bg-slate-100 rounded-full h-2">
                  <div className="bg-black h-2 rounded-full" style={{ width: `${(level.count / 856) * 100}%` }} />
                </div>
                <span className="text-sm font-medium w-12 text-right">{level.count}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RevQDashboard;
